import { signal } from "@preact/signals-react"
import { useEffect } from "react"
import { nanoid } from "nanoid"

import { fetchComponentConfig } from "../utils/api"
import { isEmptyObject } from "../utils/utils"

export const componentConfigData = signal({})
const componentPermanentId = signal(null)
export const error = signal(null)
export const loading = signal(false)
export const modified = signal(false) // allows a component to flag the data as modified so subsequent calls to the hook will reload it
export const parameterHash = signal(null)

const fetchData = async (businessEntityId, utg, config, accessToken) => {

    const hash = await import('object-hash').then(module => module.default || module)
    // if any parameters have changed then flag as modified so that the hook will reload the data
    const currentParametersHash = hash([businessEntityId, utg, config, accessToken])
    if (currentParametersHash != parameterHash.value) {
        modified.value = true
        parameterHash.value = currentParametersHash
    }

    // short-circuit if we have a pending request, are waiting for parameters or have already received an error
    if (loading.value || !businessEntityId || !utg || isEmptyObject(utg) || !config || !accessToken || error.value || !modified.value) {
        return
    }

    // short-circuit if we already have the data and it's not flagged as modified
    if (componentPermanentId.value == utg.ComponentPermanentId && !modified.value) {
        return
    }

    try {
        loading.value = true
        componentConfigData.value = await fetchComponentConfig(businessEntityId, utg.MachinePermanentId, utg.ComponentPermanentId, config, accessToken)

        componentPermanentId.value = utg.ComponentPermanentId; // allows us to short-circuit future calls when the data is already loaded

        // make sure we have apiInterfaceConfig and deviceConfig objects and child arrays
        componentConfigData.value.componentConfig = componentConfigData.value.componentConfig || {}
        componentConfigData.value.componentConfig.config = componentConfigData.value.componentConfig.config || {}
        componentConfigData.value.componentConfig.config.apiInterfaceConfig = componentConfigData.value.componentConfig.config.apiInterfaceConfig || {}
        componentConfigData.value.componentConfig.config.apiInterfaceConfig.apiInterface = componentConfigData.value.componentConfig.config.apiInterfaceConfig.apiInterface || []

        componentConfigData.value.componentConfig.config.deviceConfig = componentConfigData.value.componentConfig.config.deviceConfig || {}
        componentConfigData.value.componentConfig.config.deviceConfig.device = componentConfigData.value.componentConfig.config.deviceConfig.device || []

        // api interfaces need an id assigned
        let apiInterfaces = componentConfigData.value.componentConfig.config.apiInterfaceConfig.apiInterface
        apiInterfaces.map((apiInterface) => {
            apiInterface.ccid = apiInterface.ccid ? apiInterface.ccid : nanoid()
        })

        // make sure that this value is not -1 or UTG will break when config is pushed
        const lowDiskSpaceThresholdMB = componentConfigData.value?.componentConfig?.config?.utgGlobal?.lowDiskSpaceThresholdMB
        if (lowDiskSpaceThresholdMB && lowDiskSpaceThresholdMB == -1) {
            componentConfigData.value.componentConfig.config.utgGlobal.lowDiskSpaceThresholdMB = 2
        }
    }
    catch (err) {
        error.value = err
        console.log(err)
    }
    finally {
        loading.value = false
        modified.value = false
    }

}

export default function useComponentConfigData(businessEntityId, utg, config, accessToken) {
    useEffect(() => {
        fetchData(businessEntityId, utg, config, accessToken)
    }, [businessEntityId, utg, config, accessToken])

    return {
        componentConfigData, loading, error, modified
    }
}